import type { LootTableType, EntryType, ConditionType, FunctionType } from './types';
// Canonical version comparator (was previously re-implemented locally). Re-exported
// below so existing `import { isVersionAtLeast } from './constants'` sites keep working.
import { isVersionAtLeast, compareVersions } from '@/app/lib/versions';
export { isVersionAtLeast };

/**
 * The datapack DIRECTORY a loot table goes in: `loot_tables` before 1.21, `loot_table` from 1.21.
 *
 * ⚠️ **Getting this wrong fails completely silently.** The game does not warn about an unknown
 * directory — it simply never loads the file, so the datapack looks correct, the JSON is valid, and
 * the table does nothing. There is no error to search for, which is why the wrong name can sit in
 * the docs indefinitely (this page's own FAQ said `loot_tables` while its output panel said
 * `loot_table`, so one of the two was always wrong whichever version you were on).
 *
 * `verify:lootdatapack` asserts this against the jar for every version — it reads the directory
 * VANILLA actually uses rather than trusting this constant, so the two cannot drift.
 */
export function lootTableDirectory(version: string): 'loot_table' | 'loot_tables' {
  return isVersionAtLeast(version, '1.21') ? 'loot_table' : 'loot_tables';
}

// ── Table Types ─────────────────────────────────────────────────────────

export interface TableTypeInfo {
  id: LootTableType;
  displayName: string;
  description: string;
  /**
   * First version whose `LootContextParamSets` registers this type — read from each jar's constant
   * pool by `scripts/loot-context-sets.ts` and checked by verify:loot. A table typed with a name the
   * version lacks does not load.
   */
  minVersion?: string;
}

export const TABLE_TYPES: TableTypeInfo[] = [
  { id: 'minecraft:generic', displayName: 'Generic', description: 'General purpose loot table' },
  { id: 'minecraft:chest', displayName: 'Chest', description: 'Container loot (chests, barrels, etc.)' },
  { id: 'minecraft:block', displayName: 'Block', description: 'Block drops when broken' },
  { id: 'minecraft:entity', displayName: 'Entity', description: 'Entity death drops' },
  { id: 'minecraft:fishing', displayName: 'Fishing', description: 'Fishing rewards' },
  { id: 'minecraft:gift', displayName: 'Gift', description: 'Villager gifts, cat morning gifts' },
  { id: 'minecraft:archaeology', displayName: 'Archaeology', description: 'Suspicious block excavation', minVersion: '1.19.4' },
  { id: 'minecraft:vault', displayName: 'Vault', description: 'Trial vault drops', minVersion: '1.20.5' },
  { id: 'minecraft:equipment', displayName: 'Equipment', description: 'Gear a trial spawner gives its mobs', minVersion: '1.20.5' },
  { id: 'minecraft:shearing', displayName: 'Shearing', description: 'Shearing drops (sheep, mooshroom, bogged)', minVersion: '1.20.5' },
  { id: 'minecraft:entity_interact', displayName: 'Entity Interact', description: 'Using an item on a mob (vanilla: brushing an armadillo)', minVersion: '1.21.9' },
  { id: 'minecraft:block_interact', displayName: 'Block Interact', description: 'Using a block (vanilla: carving a pumpkin, harvesting berries, cave vines, beehives)', minVersion: '1.21.9' },
  { id: 'minecraft:barter', displayName: 'Barter', description: 'Piglin bartering' },
  { id: 'minecraft:command', displayName: 'Command', description: '/loot command context' },
  { id: 'minecraft:advancement_reward', displayName: 'Advancement Reward', description: 'Advancement completion rewards' },
  { id: 'minecraft:advancement_entity', displayName: 'Advancement Entity', description: 'Advancement entity rewards' },
  { id: 'minecraft:empty', displayName: 'Empty', description: 'Empty loot table (no drops)' },
];

export const TABLE_TYPE_MAP = new Map(TABLE_TYPES.map(t => [t.id, t]));

/** The table types `version` accepts. */
export function tableTypesFor(version: string): TableTypeInfo[] {
  return TABLE_TYPES.filter(t => !t.minVersion || isVersionAtLeast(version, t.minVersion));
}

// ── Entry Types ─────────────────────────────────────────────────────────

export interface EntryTypeInfo {
  id: EntryType;
  displayName: string;
  description: string;
  isSingleton: boolean;
  /** First version that has this entry type. Undefined = present in every supported version. */
  minVersion?: string;
}

export const ENTRY_TYPES: EntryTypeInfo[] = [
  { id: 'minecraft:item', displayName: 'Item', description: 'Drop a specific item', isSingleton: true },
  { id: 'minecraft:tag', displayName: 'Tag', description: 'Drop from an item tag', isSingleton: true },
  { id: 'minecraft:loot_table', displayName: 'Loot Table', description: 'Reference another loot table', isSingleton: true },
  { id: 'minecraft:dynamic', displayName: 'Dynamic', description: 'Dynamic content (container contents)', isSingleton: true },
  { id: 'minecraft:empty', displayName: 'Empty', description: 'No drop (used for weighted nothing)', isSingleton: true },
  { id: 'minecraft:alternatives', displayName: 'Alternatives', description: 'First matching child entry', isSingleton: false },
  { id: 'minecraft:group', displayName: 'Group', description: 'All child entries (if conditions pass)', isSingleton: false },
  { id: 'minecraft:sequence', displayName: 'Sequence', description: 'Children in order until one fails', isSingleton: false },
  // SlotLoot extends LootPoolSingletonContainer, so it is a singleton despite naming a container.
  // ⚠️ minecraft:slots is NOT offered. Every shape the builder can construct is refused by a real
  // 26.2 server: `{type:"minecraft:contents",component:"minecraft:container"}` inline, the same
  // nested under `value`, an unnamespaced type, and the nested-source forms were all tested against
  // the game (scripts/verify-loot-datapack.ts covers entry types now). Only
  // `{type:"minecraft:empty"}` loads, and an empty source yields no items, so it is useless in a
  // loot table. No vanilla loot table uses this entry, so there is no example to copy either.
  // Offering a type that can never be exported is worse than not offering it. Restore this line
  // once the slot_source payload shape is known.
  // { id: 'minecraft:slots', displayName: 'Slots', description: 'Draw from the slots of a container', isSingleton: true, minVersion: '1.21.11' },
];

export const ENTRY_TYPE_MAP = new Map(ENTRY_TYPES.map(e => [e.id, e]));

// ── Condition Types ─────────────────────────────────────────────────────

export interface ConditionTypeInfo {
  id: ConditionType;
  displayName: string;
  description: string;
  category: 'Chance' | 'Entity' | 'World' | 'Tool' | 'Logic' | 'Other';
  minVersion?: string;
  /** First version that NO LONGER accepts this condition (valid while version < removedIn). */
  removedIn?: string;
}

export const CONDITION_TYPES: ConditionTypeInfo[] = [
  // Chance
  { id: 'minecraft:random_chance', displayName: 'Random Chance', description: 'Pass with a fixed probability', category: 'Chance' },
  // Renamed in 24w18a (-> 1.21), verified against the snapshot changelog: the old name is
  // `random_chance_with_looting` and the new one drops `looting_multiplier` and makes `chance` a
  // number provider. Only the NEW name was listed, and ungated — so it was offered on 1.17-1.20.6
  // where it does not exist, and the old name was unavailable to those versions at all.
  { id: 'minecraft:random_chance_with_looting', displayName: 'Random Chance (Looting)', description: 'Chance modified by Looting level', category: 'Chance', removedIn: '1.21' },
  { id: 'minecraft:random_chance_with_enchanted_bonus', displayName: 'Random Chance (Enchanted)', description: 'Chance modified by enchantment level', category: 'Chance', minVersion: '1.21' },
  { id: 'minecraft:table_bonus', displayName: 'Table Bonus', description: 'Chance array indexed by enchantment level', category: 'Chance' },
  { id: 'minecraft:survives_explosion', displayName: 'Survives Explosion', description: 'Chance to survive based on explosion radius', category: 'Chance' },

  // Entity
  { id: 'minecraft:killed_by_player', displayName: 'Killed by Player', description: 'Entity was killed by a player', category: 'Entity' },
  { id: 'minecraft:entity_properties', displayName: 'Entity Properties', description: 'Check entity predicate', category: 'Entity' },
  { id: 'minecraft:entity_scores', displayName: 'Entity Scores', description: 'Check entity scoreboard values', category: 'Entity' },
  { id: 'minecraft:damage_source_properties', displayName: 'Damage Source', description: 'Check damage source predicate', category: 'Entity' },

  // World
  { id: 'minecraft:location_check', displayName: 'Location Check', description: 'Check location predicate', category: 'World' },
  { id: 'minecraft:time_check', displayName: 'Time Check', description: 'Check game time value', category: 'World' },
  { id: 'minecraft:weather_check', displayName: 'Weather Check', description: 'Check rain/thunder state', category: 'World' },
  // 26.3 split value_check in two (minecraft:loot_condition_type in the 26.3 registry report: both
  // new ids present, value_check gone). value_check always compared an INTEGER — its 26.2 test reads
  // `value.getInt()` — so int_value_check is its exact successor and the serializer converts an
  // old value_check to it on 26.3 (`range` becomes `test`).
  // value_check, set_enchantments and set_banner_pattern arrived in 1.17 (absent from 1.16.5's
  // loot_condition_type / loot_function_type registries) — like number providers themselves.
  { id: 'minecraft:value_check', displayName: 'Value Check', description: 'Check a number provider value against a range', category: 'World', minVersion: '1.17', removedIn: '26.3' },
  { id: 'minecraft:int_value_check', displayName: 'Int Value Check', description: 'Check a whole-number value against a range', category: 'World', minVersion: '26.3' },
  { id: 'minecraft:float_value_check', displayName: 'Float Value Check', description: 'Check a decimal value against a range', category: 'World', minVersion: '26.3' },

  // Tool
  { id: 'minecraft:match_tool', displayName: 'Match Tool', description: 'Check the tool used to break block', category: 'Tool' },
  // block_state_property -> match_block at 26.3: `block` + `properties` became a block PREDICATE
  // (`blocks` + `state`, MatchBlock's codec is BlockPredicate.MAP_CODEC). Converted on export too.
  { id: 'minecraft:block_state_property', displayName: 'Block State', description: 'Check block state properties', category: 'Tool', removedIn: '26.3' },
  { id: 'minecraft:match_block', displayName: 'Match Block', description: 'Check the broken block and its state properties', category: 'Tool', minVersion: '26.3' },
  { id: 'minecraft:enchantment_active_check', displayName: 'Enchantment Active', description: 'Check if enchantment effect is active', category: 'Tool', minVersion: '1.21' },

  // Logic
  { id: 'minecraft:inverted', displayName: 'Inverted (NOT)', description: 'Negate a condition', category: 'Logic' },
  // 1.20 replaced `alternative` with `any_of` and introduced `all_of` alongside it. Both were
  // offered unversioned, so 1.17-1.19.4 were shown two combinators that do not exist in them
  // while `alternative` — which vanilla's own leaf tables use in every one of those versions —
  // could not be expressed at all. Ranges taken from minecraft:loot_condition_type per version.
  { id: 'minecraft:all_of', displayName: 'All Of (AND)', description: 'All sub-conditions must pass', category: 'Logic', minVersion: '1.20' },
  { id: 'minecraft:any_of', displayName: 'Any Of (OR)', description: 'At least one sub-condition must pass', category: 'Logic', minVersion: '1.20' },
  { id: 'minecraft:alternative', displayName: 'Alternative (OR)', description: 'At least one sub-condition must pass — the pre-1.20 name for Any Of', category: 'Logic', removedIn: '1.20' },

  // Other
  // From 26.3 there is no `reference` condition TYPE — a condition slot takes a predicate id string
  // directly (vanilla: "condition": "minecraft:tool/can_shear"). Still offered: the serializer
  // writes it as that bare id (BARE_REFERENCE_FROM).
  { id: 'minecraft:reference', displayName: 'Reference', description: 'Reference an external predicate', category: 'Other' },
  { id: 'minecraft:environment_attribute_check', displayName: 'Environment Attribute', description: 'Check a dimension environment attribute against a value', category: 'World', minVersion: '26.1' },
];

export const CONDITION_TYPE_MAP = new Map(CONDITION_TYPES.map(c => [c.id, c]));

export const CONDITION_CATEGORIES = ['Chance', 'Entity', 'World', 'Tool', 'Logic', 'Other'] as const;

// ── Function Types ──────────────────────────────────────────────────────

export interface FunctionTypeInfo {
  id: FunctionType;
  displayName: string;
  description: string;
  category: 'Count' | 'Enchantment' | 'Display' | 'NBT/Components' | 'Container' | 'Special' | 'Meta';
  minVersion?: string;
  /**
   * First version that NO LONGER accepts this function (i.e. valid while version < removedIn).
   * The model previously only had a lower bound, so a renamed function stayed on offer forever:
   * `looting_enchant` and its 1.21 replacement `enchanted_count_increase` were BOTH always
   * listed, so whichever version you picked, one of them produced an invalid loot table.
   */
  removedIn?: string;
}

export const FUNCTION_TYPES: FunctionTypeInfo[] = [
  // Count
  { id: 'minecraft:set_count', displayName: 'Set Count', description: 'Set item stack count', category: 'Count' },
  { id: 'minecraft:set_damage', displayName: 'Set Damage', description: 'Set item damage (0=full, 1=none)', category: 'Count' },
  { id: 'minecraft:limit_count', displayName: 'Limit Count', description: 'Clamp count to a range', category: 'Count' },
  { id: 'minecraft:explosion_decay', displayName: 'Explosion Decay', description: 'Random chance to drop per explosion', category: 'Count' },
  { id: 'minecraft:looting_enchant', displayName: 'Looting Enchant', description: 'Add count per looting level', category: 'Count', removedIn: '1.21' },
  { id: 'minecraft:enchanted_count_increase', displayName: 'Enchanted Count Increase', description: 'Add count per enchantment level', category: 'Count', minVersion: '1.21' },
  { id: 'minecraft:apply_bonus', displayName: 'Apply Bonus', description: 'Apply fortune-like bonus formula', category: 'Count' },

  // Enchantment
  { id: 'minecraft:enchant_with_levels', displayName: 'Enchant with Levels', description: 'Apply random enchantments at a level', category: 'Enchantment' },
  { id: 'minecraft:enchant_randomly', displayName: 'Enchant Randomly', description: 'Apply a single random enchantment', category: 'Enchantment' },
  { id: 'minecraft:set_enchantments', displayName: 'Set Enchantments', description: 'Set specific enchantments and levels', category: 'Enchantment', minVersion: '1.17' },

  // Display
  { id: 'minecraft:set_name', displayName: 'Set Name', description: 'Set custom display name', category: 'Display' },
  { id: 'minecraft:copy_name', displayName: 'Copy Name', description: 'Copy name from block entity', category: 'Display' },
  { id: 'minecraft:set_lore', displayName: 'Set Lore', description: 'Set item lore lines', category: 'Display' },
  { id: 'minecraft:set_attributes', displayName: 'Set Attributes', description: 'Add attribute modifiers', category: 'Display' },
  { id: 'minecraft:toggle_tooltips', displayName: 'Toggle Tooltips', description: 'Show/hide tooltip components', category: 'Display', minVersion: '1.20.5' },
  { id: 'minecraft:set_custom_model_data', displayName: 'Set Custom Model Data', description: 'Set custom model data values', category: 'Display', minVersion: '1.20.5' },

  // NBT/Components
  { id: 'minecraft:set_custom_data', displayName: 'Set Custom Data', description: 'Set custom NBT data', category: 'NBT/Components', minVersion: '1.20.5' },
  { id: 'minecraft:set_nbt', displayName: 'Set NBT', description: 'Set custom NBT data — the pre-1.20.5 name for Set Custom Data', category: 'NBT/Components', removedIn: '1.20.5' },
  { id: 'minecraft:set_components', displayName: 'Set Components', description: 'Set item data components', category: 'NBT/Components', minVersion: '1.20.5' },
  { id: 'minecraft:copy_custom_data', displayName: 'Copy Custom Data', description: 'Copy NBT from source to item', category: 'NBT/Components', minVersion: '1.20.5' },
  { id: 'minecraft:copy_nbt', displayName: 'Copy NBT', description: 'Copy NBT from source to item — the pre-1.20.5 name for Copy Custom Data', category: 'NBT/Components', removedIn: '1.20.5' },
  { id: 'minecraft:copy_components', displayName: 'Copy Components', description: 'Copy components from block entity', category: 'NBT/Components', minVersion: '1.20.5' },
  { id: 'minecraft:copy_state', displayName: 'Copy State', description: 'Copy block state properties to item', category: 'NBT/Components' },

  // Container
  { id: 'minecraft:set_contents', displayName: 'Set Contents', description: 'Set container contents', category: 'Container' },
  { id: 'minecraft:modify_contents', displayName: 'Modify Contents', description: 'Modify existing container contents', category: 'Container', minVersion: '1.20.5' },
  { id: 'minecraft:set_loot_table', displayName: 'Set Loot Table', description: 'Assign a loot table to container', category: 'Container' },

  // Special
  { id: 'minecraft:set_potion', displayName: 'Set Potion', description: 'Set potion type', category: 'Special', minVersion: '1.18' },
  { id: 'minecraft:set_stew_effect', displayName: 'Set Stew Effect', description: 'Set suspicious stew effects', category: 'Special' },
  { id: 'minecraft:set_ominous_bottle_amplifier', displayName: 'Set Ominous Bottle', description: 'Set ominous bottle amplifier', category: 'Special', minVersion: '1.20.5' },
  { id: 'minecraft:exploration_map', displayName: 'Exploration Map', description: 'Create a treasure map', category: 'Special' },
  { id: 'minecraft:fill_player_head', displayName: 'Fill Player Head', description: 'Set player head skin', category: 'Special' },
  { id: 'minecraft:furnace_smelt', displayName: 'Furnace Smelt', description: 'Smelt the item (like Silk Touch inverse)', category: 'Special' },
  { id: 'minecraft:set_fireworks', displayName: 'Set Fireworks', description: 'Set firework rocket properties', category: 'Special', minVersion: '1.20.5' },
  { id: 'minecraft:set_firework_explosion', displayName: 'Set Firework Explosion', description: 'Set firework star explosion properties', category: 'Special', minVersion: '1.20.5' },
  { id: 'minecraft:set_book_cover', displayName: 'Set Book Cover', description: 'Set written book cover info', category: 'Special', minVersion: '1.20.5' },
  { id: 'minecraft:set_writable_book_pages', displayName: 'Set Writable Pages', description: 'Set book and quill pages', category: 'Special', minVersion: '1.20.5' },
  { id: 'minecraft:set_written_book_pages', displayName: 'Set Written Pages', description: 'Set written book pages', category: 'Special', minVersion: '1.20.5' },
  { id: 'minecraft:set_instrument', displayName: 'Set Instrument', description: 'Set goat horn instrument', category: 'Special', minVersion: '1.19' },
  { id: 'minecraft:set_banner_pattern', displayName: 'Set Banner Pattern', description: 'Set banner patterns', category: 'Special', minVersion: '1.17' },
  { id: 'minecraft:set_item', displayName: 'Set Item', description: 'Replace the item, keeping its components', category: 'Special', minVersion: '1.20.5' },
  { id: 'minecraft:set_random_dyes', displayName: 'Set Random Dyes', description: 'Dye the item with random colors', category: 'Special', minVersion: '26.1' },
  { id: 'minecraft:set_random_potion', displayName: 'Set Random Potion', description: 'Set a random potion from a list', category: 'Special', minVersion: '26.1' },

  // Meta — all three arrived after 1.17 and were previously offered on every version.
  { id: 'minecraft:filtered', displayName: 'Filtered', description: 'Apply functions based on item filter', category: 'Meta', minVersion: '1.20.5' },
  // Like the condition: from 26.3 a modifier slot takes an item_modifier id string directly.
  { id: 'minecraft:reference', displayName: 'Reference', description: 'Reference an external item modifier', category: 'Meta', minVersion: '1.20' },
  { id: 'minecraft:sequence', displayName: 'Sequence', description: 'Apply a sequence of functions', category: 'Meta', minVersion: '1.20.2' },
  { id: 'minecraft:discard', displayName: 'Discard', description: 'Drop the item entirely (no parameters)', category: 'Meta', minVersion: '1.21.11' },
];

export const FUNCTION_TYPE_MAP = new Map(FUNCTION_TYPES.map(f => [f.id, f]));

export const FUNCTION_CATEGORIES = ['Count', 'Enchantment', 'Display', 'NBT/Components', 'Container', 'Special', 'Meta'] as const;

// ── Apply Bonus Formulas ────────────────────────────────────────────────

export const APPLY_BONUS_FORMULAS = [
  { id: 'minecraft:ore_drops', displayName: 'Ore Drops', description: 'Standard ore fortune formula' },
  { id: 'minecraft:uniform_bonus_count', displayName: 'Uniform Bonus', description: 'Add 0 to (level * multiplier) items' },
  { id: 'minecraft:binomial_with_bonus_count', displayName: 'Binomial Bonus', description: 'Binomial distribution with bonus count' },
] as const;

/**
 * The REQUIRED `parameters` of each apply_bonus formula (ore_drops takes none). Defaults are the
 * vanilla values, read from the jars' block loot tables (1.17 and 26.2 agree): glowstone/melon
 * `bonusMultiplier: 1`, wheat/carrots/potatoes `extra: 3, probability: 0.5714286`.
 */
export const APPLY_BONUS_DEFAULT_PARAMETERS: Record<string, Record<string, number>> = {
  'minecraft:uniform_bonus_count': { bonusMultiplier: 1 },
  'minecraft:binomial_with_bonus_count': { extra: 3, probability: 0.5714286 },
};

// ── Dynamic Entry Names ─────────────────────────────────────────────────

// `sherds` arrived with the decorated pot's drop in 1.20 (vanilla-mcdoc `#[since="1.20"]`; 1.19.4's
// decorated_pot table is empty, 1.20.1's names minecraft:sherds).
export const DYNAMIC_ENTRY_NAMES: readonly { id: string; displayName: string; minVersion?: string }[] = [
  { id: 'minecraft:contents', displayName: 'Contents' },
  { id: 'minecraft:sherds', displayName: 'Sherds', minVersion: '1.20' },
];

/**
 * A table's `random_sequence` exists from 1.20 (vanilla-mcdoc `#[since="1.20"]`; all 1091 vanilla
 * 1.20.1 tables carry one, no 1.19.4 table does). Before that it is left out of the export.
 */
export const RANDOM_SEQUENCE_FROM = '1.20';

// ── Entity Targets ──────────────────────────────────────────────────────

export const ENTITY_TARGETS = [
  { id: 'this', displayName: 'This (self)' },
  { id: 'attacker', displayName: 'Attacker' },
  { id: 'direct_attacker', displayName: 'Direct Attacker' },
  { id: 'attacking_player', displayName: 'Attacking Player' },
] as const;

// ── Copy Source Targets ─────────────────────────────────────────────────

export const COPY_SOURCES = [
  { id: 'block_entity', displayName: 'Block Entity' },
  { id: 'this', displayName: 'This Entity' },
  { id: 'attacker', displayName: 'Attacker' },
  { id: 'attacking_player', displayName: 'Attacking Player' },
] as const;

// ── Attribute Operations ────────────────────────────────────────────────

export const ATTRIBUTE_OPERATIONS = [
  { id: 'add_value', displayName: 'Add' },
  { id: 'add_multiplied_base', displayName: 'Multiply Base' },
  { id: 'add_multiplied_total', displayName: 'Multiply Total' },
] as const;

// ── Equipment Slots ─────────────────────────────────────────────────────

export const EQUIPMENT_SLOTS = [
  'mainhand', 'offhand', 'head', 'chest', 'legs', 'feet',
] as const;

// ── Map Decorations ─────────────────────────────────────────────────────

/**
 * Map decorations per version — the `MapDecoration$Type` enum before 1.20.5, the
 * `minecraft:map_decoration_type` registry from 1.20.5 (verify:loot compares these lists with that
 * registry, both directions, from 1.20.5; the enum era before it is NOT checked). The village and
 * temple icons arrived in 1.20.2, trial_chambers in 1.20.5. The old single list offered
 * jungle_temple/swamp_hut/trial_chambers everywhere and lacked the villages and 15 banner colors.
 */
const BASE_DECORATIONS = [
  'red_x', 'mansion', 'monument', 'player', 'frame', 'red_marker', 'blue_marker', 'target_x',
  'target_point', 'player_off_map', 'player_off_limits',
  ...['white', 'orange', 'magenta', 'light_blue', 'yellow', 'lime', 'pink', 'gray', 'light_gray',
    'cyan', 'purple', 'blue', 'brown', 'green', 'red', 'black'].map(c => `banner_${c}`),
];
const DECORATIONS_1_20_2 = ['village_desert', 'village_plains', 'village_savanna', 'village_snowy', 'village_taiga', 'jungle_temple', 'swamp_hut'];

export function mapDecorationsFor(version: string): string[] {
  return [
    ...BASE_DECORATIONS,
    ...(isVersionAtLeast(version, '1.20.2') ? DECORATIONS_1_20_2 : []),
    ...(isVersionAtLeast(version, '1.20.5') ? ['trial_chambers'] : []),
    // 26.3 (26.2 vs 26.3 registry reports; vanilla's new abandoned-camp maps use them).
    ...(isVersionAtLeast(version, '26.3') ? ['abandoned_camp', 'ancient_city', 'desert_pyramid', 'mineshaft', 'ocean_ruin_warm'] : []),
  ];
}

/**
 * exploration_map `destination`: a structure TAG from 1.18.2, before that a structure NAME (the 1.17
 * deserializer looks it up by name and, finding no `on_treasure_maps`, logs an error and silently
 * uses buried treasure). The vanilla tags map to these names.
 */
export const LEGACY_MAP_DESTINATIONS: Record<string, string> = {
  on_treasure_maps: 'buried_treasure',
  on_woodland_explorer_maps: 'mansion',
  on_ocean_explorer_maps: 'monument',
};

/**
 * 26.3 RENAMED five exploration-map structure tags. Each pair holds the same single structure (26.2
 * vs 26.3 jars, `data/minecraft/tags/worldgen/structure`); the old names are gone from 26.3, so a
 * 26.2 destination exported unchanged points at a tag that does not exist.
 */
export const MAP_TAGS_RENAMED_26_3: Record<string, string> = {
  on_jungle_explorer_maps: 'on_jungle_pyramid_maps',
  on_ocean_explorer_maps: 'on_ocean_monument_maps',
  on_swamp_explorer_maps: 'on_swamp_hut_maps',
  on_trial_chambers_maps: 'on_buried_trial_chambers_maps',
  on_woodland_explorer_maps: 'on_woodland_mansion_maps',
};

/**
 * Vanilla structure tags per version, from each jar's `data/minecraft/tags/worldgen/structure`
 * (`verify:loot` compares this with every jar, both directions). Empty before 1.18.2: the
 * destination was a structure name then. The 1.20.2 village/jungle/swamp map tags lived only in the
 * trade_rebalance experiment pack until 1.21.5 made them vanilla.
 */
export function structureTagsFor(version: string): string[] {
  if (!isVersionAtLeast(version, '1.18.2')) return [];
  const shared = ['dolphin_located', 'eye_of_ender_located', 'mineshaft', 'ocean_ruin', 'ruined_portal', 'shipwreck', 'village', 'on_treasure_maps',
    ...(isVersionAtLeast(version, '1.19') ? ['cats_spawn_as_black', 'cats_spawn_in'] : []),
    ...(isVersionAtLeast(version, '1.21.5') ? ['on_desert_village_maps', 'on_plains_village_maps', 'on_savanna_village_maps', 'on_snowy_village_maps', 'on_taiga_village_maps'] : []),
  ];
  if (isVersionAtLeast(version, '26.3')) {
    return [...shared, 'abandoned_camp', 'on_ancient_city_maps', 'on_desert_pyramid_maps', 'on_mineshaft_maps', 'on_ocean_ruin_warm_maps',
      ...['bamboo_jungle', 'birch_forest', 'cherry_grove', 'dappled_forest', 'flower_forest', 'pale_garden', 'swamp', 'windswept_forest'].map(b => `on_abandoned_camp_${b}`),
      ...Object.values(MAP_TAGS_RENAMED_26_3)];
  }
  return [...shared, 'on_woodland_explorer_maps', 'on_ocean_explorer_maps',
    ...(isVersionAtLeast(version, '1.21') ? ['on_trial_chambers_maps'] : []),
    ...(isVersionAtLeast(version, '1.21.5') ? ['on_jungle_explorer_maps', 'on_swamp_explorer_maps'] : [])];
}

/** The `on_*_maps` tags of a version, for the destination hint. */
export function mapDestinationTagsFor(version: string): string[] {
  return structureTagsFor(version).filter(t => t.startsWith('on_')).sort();
}

/**
 * A `minecraft:` destination tag in the name `version` uses: an old name becomes its 26.3 name from
 * 26.3, and a 26.3 name its old one before (a table built on one side and exported on the other).
 * Other namespaces and tags with no counterpart pass through unchanged — the validator flags those.
 */
export function mapDestinationFor(destination: string, version: string): string {
  const m = /^(#?)(minecraft:)?([a-z0-9_./-]+)$/.exec(destination);
  if (!m) return destination;
  const [, hash, ns = '', bare] = m;
  const renamed = isVersionAtLeast(version, '26.3')
    ? MAP_TAGS_RENAMED_26_3[bare]
    : Object.entries(MAP_TAGS_RENAMED_26_3).find(([, to]) => to === bare)?.[0];
  return renamed ? `${hash}${ns}${renamed}` : destination;
}

// ── Version Gating ──────────────────────────────────────────────────────

/**
 * 1.17 introduced NUMBER PROVIDERS (`loot_number_provider_type` does not exist in 1.16.5's registries):
 * `score` arrived with them, as did set_count / set_damage's `add` (vanilla-mcdoc `#[since="1.17"]`),
 * and match_tool's item predicate took a single `item` (vanilla 1.16.5: `"item": "minecraft:shears"`,
 * 30 tables) before 1.17's `items` list.
 */
export const NUMBER_PROVIDERS_FROM = '1.17';

// Both bounds must be applied. Filtering on minVersion ALONE is what shipped, and it meant a
// RENAMED entry stayed on offer forever: `looting_enchant` carries removedIn '1.21' and the picker
// ignored it, so on 1.21+ the tool still offered a function the game had dropped. The validator
// flagged it afterwards, which is a worse experience than simply not offering it.
const inVersion = (v: string, e: { minVersion?: string; removedIn?: string }) =>
  (!e.minVersion || isVersionAtLeast(v, e.minVersion)) &&
  (!e.removedIn || !isVersionAtLeast(v, e.removedIn));

/**
 * 26.3 REWROTE the loot-table format; the serializer writes it from `LOOT_FORMAT_26_3` on (see
 * `serializer.ts`, and the loot section of `.claude/rules/minecraft-version-formats.md`).
 */
export const LOOT_FORMAT_26_3 = '26.3';

/**
 * From 26.3 the `reference` condition and the `reference` function are not registry types any more:
 * a condition / modifier slot takes a predicate or item_modifier id STRING. Both stay on offer, and
 * the serializer writes that bare id. verify:loot exempts them from the registry comparison there
 * and instead asserts the serialized value IS a string.
 */
export const BARE_REFERENCE_FROM = '26.3';

/**
 * 🚨 The NEWEST version whose loot-table format has been VERIFIED — against Mojang's own tables and
 * registry report (`npm run verify:loot`) AND a real server loading the builder's output
 * (`npm run verify:lootdatapack`). Anything NEWER is held back: the tool offers nothing there and
 * the page shows `LootVersionGate` instead of exporting.
 *
 * Why a ceiling and not "the newest era we know": 26.3 rewrote the format with no warning, and the
 * game IGNORES keys it does not know — the 26.2-shaped table loads on 26.3 and silently drops every
 * function and condition. A snapshot registered as a newer version would otherwise get the newest
 * known era's output with nothing ever having checked it. Raising this is a deliberate act: run both
 * loot gates on the new version first (a 26.4 snapshot registered as `26.4` stays gated until then).
 */
//
// 26.4 (2026-10-31, against 26.4-snapshot-6): the loot format is 26.3's. The loot, criterion,
// predicate and enchantment classes' codec strings are identical between the 26.3 and snapshot-6 jars
// (459 classes; only a StatePropertiesPredicate helper moved), and the loot type registries match.
// Two registry changes reach loot: `environment_attribute` gains `visual/has_sky_occluder`, and
// `fluid` drops `flowing_water` / `flowing_lava` (a fluid predicate naming one is refused — the
// validator flags it). Both loot gates pass on a real snapshot-6 server.
export const LOOT_NEWEST_VERIFIED = '26.4';
/** The newest version the builder can export for — where the gate's "switch" button goes. */
export const LOOT_LATEST_SUPPORTED = LOOT_NEWEST_VERIFIED;

export function isLootVersionSupported(version: string): boolean {
  return compareVersions(version, LOOT_NEWEST_VERIFIED) <= 0;
}

export function getAvailableConditions(version: string): ConditionTypeInfo[] {
  if (!isLootVersionSupported(version)) return [];
  return CONDITION_TYPES.filter(c => inVersion(version, c));
}

export function getAvailableFunctions(version: string): FunctionTypeInfo[] {
  if (!isLootVersionSupported(version)) return [];
  return FUNCTION_TYPES.filter(f => inVersion(version, f));
}

export function getAvailableEntryTypes(version: string): EntryTypeInfo[] {
  if (!isLootVersionSupported(version)) return [];
  return ENTRY_TYPES.filter(e => inVersion(version, e));
}

/**
 * Dispatch option lists — the `attribute` of environment_attribute_check and the `slot_source` of
 * the slots entry both select a type from a registry, and the remaining fields depend on which.
 *
 * Both lists are copied from Mojang's own registry report (`minecraft:environment_attribute`,
 * `minecraft:slot_source_type`) and verify-loot-format.ts re-checks them against it, so a hardcoded
 * list here cannot quietly drift from the game the way an unaudited option list would.
 */
export const ENVIRONMENT_ATTRIBUTES = [
  'minecraft:audio/ambient_sounds', 'minecraft:audio/background_music', 'minecraft:audio/firefly_bush_sounds',
  'minecraft:audio/music_volume', 'minecraft:gameplay/baby_villager_activity', 'minecraft:gameplay/bed_rule',
  'minecraft:gameplay/bees_stay_in_hive', 'minecraft:gameplay/can_pillager_patrol_spawn',
  'minecraft:gameplay/can_start_raid', 'minecraft:gameplay/cat_waking_up_gift_chance',
  'minecraft:gameplay/creaking_active', 'minecraft:gameplay/eyeblossom_open', 'minecraft:gameplay/fast_lava',
  'minecraft:gameplay/increased_fire_burnout', 'minecraft:gameplay/monsters_burn',
  'minecraft:gameplay/nether_portal_spawns_piglin', 'minecraft:gameplay/piglins_zombify',
  'minecraft:gameplay/respawn_anchor_works', 'minecraft:gameplay/sky_light_level',
  'minecraft:gameplay/snow_golem_melts', 'minecraft:gameplay/surface_slime_spawn_chance',
  'minecraft:gameplay/turtle_egg_hatch_chance', 'minecraft:gameplay/villager_activity',
  'minecraft:gameplay/water_evaporates', 'minecraft:visual/ambient_light_color',
  'minecraft:visual/ambient_particles', 'minecraft:visual/block_light_tint', 'minecraft:visual/cloud_color',
  'minecraft:visual/cloud_fog_end_distance', 'minecraft:visual/cloud_height',
  'minecraft:visual/default_dripstone_particle', 'minecraft:visual/fog_color',
  'minecraft:visual/fog_end_distance', 'minecraft:visual/fog_start_distance', 'minecraft:visual/moon_angle',
  'minecraft:visual/moon_phase', 'minecraft:visual/night_vision_color', 'minecraft:visual/sky_color',
  'minecraft:visual/sky_fog_end_distance', 'minecraft:visual/sky_light_color',
  'minecraft:visual/sky_light_factor', 'minecraft:visual/star_angle', 'minecraft:visual/star_brightness',
  'minecraft:visual/sun_angle', 'minecraft:visual/sunrise_sunset_color', 'minecraft:visual/water_fog_color',
  'minecraft:visual/water_fog_end_distance', 'minecraft:visual/water_fog_start_distance',
] as const;

/** Added to `minecraft:environment_attribute` in 26.3 (26.2 vs 26.3 registry reports). */
const ENVIRONMENT_ATTRIBUTES_26_3 = [
  'minecraft:gameplay/creature_world_gen_spawn_probability', 'minecraft:gameplay/natural_mob_spawns',
  'minecraft:gameplay/straw_bed_rule',
] as const;
// 26.4 (26.3 vs 26.4-snapshot-6 registry reports).
const ENVIRONMENT_ATTRIBUTES_26_4 = ['minecraft:visual/has_sky_occluder'] as const;

/** The environment attributes `version` registers (verify:loot compares this with the registry). */
export function environmentAttributesFor(version: string): string[] {
  return [...ENVIRONMENT_ATTRIBUTES, ...(isVersionAtLeast(version, '26.3') ? ENVIRONMENT_ATTRIBUTES_26_3 : []),
    ...(isVersionAtLeast(version, '26.4') ? ENVIRONMENT_ATTRIBUTES_26_4 : [])].sort();
}

export const SLOT_SOURCE_TYPES = [
  'minecraft:contents', 'minecraft:empty', 'minecraft:filtered',
  'minecraft:group', 'minecraft:limit_slots', 'minecraft:slot_range',
] as const;
